import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import { getQueryStringResults } from '@plone/volto/actions/querystringsearch/querystringsearch';
import { getVocabulary } from '@plone/volto/actions/vocabularies/vocabularies';
import { getBaseUrl } from '@plone/volto/helpers/Url/Url';
import {
  countQuery,
  countSubrequest,
  ITEMS_PER_PERIOD,
  itemsSubrequest,
  normalizePath,
  periodHref,
  periodOfPage,
  queryAsIs,
  querystringKey,
  selectedPeriods,
  vocabularyValues,
} from './periodTabs';

const EMPTY = [];

/**
 * A listing with a bar of periods built from the block's own query.
 *
 * The bar works two ways, decided by whether `tab_pages_base` is set.
 *
 * Without it, one page holds everything and a tab narrows the list in place
 * by setting the spec's URL parameter. The choice stays linkable and the back
 * button keeps working, which plain component state would have cost.
 *
 * With it, each period has its own page - the arrangement `/publications` and
 * `/teaching` both already have. The block on such a page is restricted to
 * that period, which on its own would leave the bar with a single tab; so the
 * counts are taken against the base instead, and the tabs become links to the
 * sibling pages. The page at the base itself is not one of those: it shows
 * the most recent periods and its tabs lead away to the individual ones, so
 * each period keeps a single address.
 *
 * See `periodTabs.js` for what a spec has to provide.
 */
const PeriodTabsListing = ({
  spec,
  card: Card,
  cardProps = {},
  containerClass = '',
  ...props
}) => {
  const {
    querystring,
    tab_count,
    tab_pages_base,
    trailing_link_label,
    trailing_link_url,
    isEditMode,
  } = props;

  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const path = getBaseUrl(location.pathname);
  const here = normalizePath(path);

  const base = normalizePath(tab_pages_base);
  const linkTabs = !!(base && spec.segment);
  const onPeriodPage = !!periodOfPage(spec, base, path);

  const key = useMemo(
    () => querystringKey(querystring, base, spec.name),
    [querystring, base, spec.name],
  );

  // Read in the effects without making them depend on it: `key` already
  // changes exactly when a new request is warranted.
  const querystringRef = useRef(querystring);
  querystringRef.current = querystring;

  // Only consulted when the query names no periods itself.
  const vocabulary = useSelector(
    (state) =>
      (spec.vocabulary && vocabularyValues(state, spec.vocabulary)) || EMPTY,
    shallowEqual,
  );
  const needsVocabulary =
    !!spec.vocabulary && !spec.periods(querystring).length;
  useEffect(() => {
    if (needsVocabulary && !vocabulary.length) {
      dispatch(getVocabulary({ vocabNameOrURL: spec.vocabulary, size: -1 }));
    }
  }, [dispatch, needsVocabulary, vocabulary.length, spec.vocabulary]);

  const periods = useMemo(
    () => spec.periods(querystring, vocabulary),
    [spec, querystring, vocabulary],
  );

  // shallowEqual: the selector builds a fresh object every call, so without
  // it this component would re-render on every dispatched action.
  const counts = useSelector((state) => {
    const subrequests = state.querystringsearch?.subrequests || {};
    const found = {};
    periods.forEach((p) => {
      found[p] = subrequests[countSubrequest(key, p)]?.total;
    });
    return found;
  }, shallowEqual);

  const countsRef = useRef(counts);
  countsRef.current = counts;

  // One small query per candidate period. They run in edit mode too: an
  // editor changing the query wants to see which periods it covers.
  const periodsKey = periods.join('|');
  useEffect(() => {
    if (!periodsKey) return;
    const scope = spec.scope
      ? spec.scope(querystringRef.current, base)
      : queryAsIs(querystringRef.current);
    periodsKey.split('|').forEach((period) => {
      if (countsRef.current[period] !== undefined) return;
      dispatch(
        getQueryStringResults(
          path,
          countQuery(spec, scope, period),
          countSubrequest(key, period),
        ),
      );
    });
  }, [dispatch, path, key, periodsKey, base, spec]);

  // Only once every count is in, so the bar and the default selection don't
  // move about while the responses land one by one.
  const allCountsKnown = periods.every((p) => counts[p] !== undefined);
  const withResults = periods.filter((p) => (counts[p] ?? 0) > 0);
  const tabs = allCountsKnown ? withResults : periods;

  const active = new URLSearchParams(location.search || '').get(spec.param);
  const shown = onPeriodPage
    ? EMPTY
    : selectedPeriods(spec, linkTabs ? '' : location.search, tabs, tab_count);
  const shownKey = shown.join('|');

  const slot = itemsSubrequest(key, onPeriodPage ? 'page' : shownKey);
  const results = useSelector(
    (state) => state.querystringsearch?.subrequests?.[slot],
  );
  const loadedRef = useRef(false);
  loadedRef.current = !!results;

  useEffect(() => {
    if (!onPeriodPage && !shownKey) return;
    if (loadedRef.current) return;
    const q = queryAsIs(querystringRef.current);
    dispatch(
      getQueryStringResults(
        path,
        {
          ...(onPeriodPage
            ? spec.withoutPeriod
              ? spec.withoutPeriod(q)
              : q
            : spec.narrow(q, shownKey.split('|'))),
          b_size: ITEMS_PER_PERIOD,
          metadata_fields: '_all',
        },
        slot,
      ),
    );
  }, [dispatch, path, slot, shownKey, onPeriodPage, spec]);

  const items = results?.items || EMPTY;

  // Grouped by period rather than by the selection, so the same rendering
  // serves both ways of using the bar.
  //
  // The groups are then cut back to what was asked for. A period is not
  // always expressible as one criterion - a semester is a year *and* a term,
  // two keyword indexes, and asking for {SS 2026, WS 2025} necessarily also
  // matches SS 2025 and WS 2026. The counts behind the tabs name a single
  // period each and are exact; only this query has to be narrowed again here.
  const groups = useMemo(() => {
    const byPeriod = new Map();
    items.forEach((item) => {
      const p = spec.of(item);
      if (!byPeriod.has(p)) byPeriod.set(p, []);
      byPeriod.get(p).push(item);
    });
    const wanted = onPeriodPage ? null : new Set(shownKey.split('|'));
    return [...byPeriod.entries()]
      .filter(([p]) => !wanted || wanted.has(p))
      .sort((a, b) => spec.compare(b[0], a[0]));
  }, [items, spec, onPeriodPage, shownKey]);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // The cards only switch to their tile shape; the columns come from the
  // container, as in `createCardListingView`.
  const containerClasses = [
    'cards__container',
    containerClass,
    cardProps.gridLayout ? 'cards__container--grid' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const trailingIsHere =
    trailing_link_url && here === normalizePath(trailing_link_url);

  const choose = (period) => (event) => {
    event.preventDefault();
    const params = new URLSearchParams(location.search || '');
    if (period) {
      params.set(spec.param, period);
    } else {
      params.delete(spec.param);
    }
    const search = params.toString();
    history.push(`${location.pathname}${search ? `?${search}` : ''}`);
  };

  const tab = (period, label) => {
    const href = linkTabs
      ? period
        ? periodHref(spec, base, period)
        : base
      : null;
    const isActive = linkTabs
      ? here === normalizePath(href)
      : period
        ? active === period
        : !active;
    const className = `publication-year-archive__nav-btn${
      isActive ? ' publication-year-archive__nav-btn--active' : ''
    }`;
    const title =
      period && counts[period]
        ? `${counts[period]} ${spec.noun || 'entries'}`
        : undefined;

    return linkTabs ? (
      <UniversalLink
        key={period || 'recent'}
        href={href}
        className={className}
        title={title}
      >
        {label}
      </UniversalLink>
    ) : (
      <button
        key={period || 'recent'}
        type="button"
        onClick={choose(period)}
        className={className}
        title={title}
      >
        {label}
      </button>
    );
  };

  return (
    <div className="view-wrapper publication-year-archive publication-year-archive--tabs">
      {(tabs.length > 1 || trailing_link_url) && (
        <nav
          className="publication-year-archive__years publication-year-archive__page-nav"
          aria-label={spec.navLabel || 'Periods'}
        >
          {tab(null, 'Recent')}
          {tabs.map((p) => tab(p, spec.label(p)))}
          {trailing_link_url && (
            <UniversalLink
              href={trailing_link_url}
              className={`publication-year-archive__nav-btn${
                trailingIsHere
                  ? ' publication-year-archive__nav-btn--active'
                  : ''
              }`}
            >
              {trailing_link_label || 'Earlier'}
            </UniversalLink>
          )}
          <button
            type="button"
            className="publication-year-archive__nav-btn publication-year-archive__nav-btn--up"
            onClick={scrollToTop}
            aria-label="Back to top"
          >
            ↑
          </button>
        </nav>
      )}

      {groups.map(([period, ofPeriod]) => (
        <section key={period} className="publication-year-archive__section">
          {/* One period on its own is already named by the active tab and by
              the block's headline, so the heading would only repeat it. */}
          {groups.length > 1 && (
            <h2 className="publication-year-archive__heading">
              {spec.label(period)}
            </h2>
          )}
          <div className={containerClasses}>
            {ofPeriod.map((item) => (
              <Card key={item['@id']} item={item} {...cardProps} />
            ))}
          </div>
        </section>
      ))}

      {results?.loaded && !items.length && (
        <p className="publication-year-archive__empty">
          {spec.noun
            ? isEditMode
              ? `This query matches no ${spec.noun}.`
              : `No ${spec.noun} found.`
            : isEditMode
              ? 'This query matches nothing.'
              : 'Nothing found.'}
        </p>
      )}
    </div>
  );
};

PeriodTabsListing.propTypes = {
  spec: PropTypes.objectOf(PropTypes.any).isRequired,
  card: PropTypes.elementType.isRequired,
  containerClass: PropTypes.string,
  cardProps: PropTypes.objectOf(PropTypes.any),
  querystring: PropTypes.objectOf(PropTypes.any),
  tab_count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  tab_pages_base: PropTypes.string,
  trailing_link_label: PropTypes.string,
  trailing_link_url: PropTypes.string,
  isEditMode: PropTypes.bool,
};

export default PeriodTabsListing;
